import styles from "./TaskPanel.module.scss";
import React, { Dispatch, FC, PropsWithChildren, ReactElement, SetStateAction, useContext } from "react";
import { Button, Flex, FlexProps } from "antd";
import cx from "classnames";
import { useLocation } from "react-router-dom";
import { CheckOutlined } from "@ant-design/icons";
import SentenceTaskHelpButton from "Components/SentenceTask/SentenceTaskHelpButton";
import { TaskType } from "App.types";
import ReportFeedback from "Components/ReportFeedback";
import { useIsBeginner } from "Hooks/useIsBeginner";
import { isIOS, isMobile } from "react-device-detect";
import { useSuspenseQuery } from "@tanstack/react-query";
import API from "Api";
import { UserContext } from "App";
import { replaceTemplates } from "App.helpers";

const TaskPanel: FC<
  Partial<FlexProps> &
    PropsWithChildren & {
      task: TaskType | undefined;
      lessonId: string | number;
      sentId: string | number | undefined;
      storyId: string | number | undefined;
      onNext?: () => any;
      setDictOpened?: Dispatch<SetStateAction<boolean>>;
      showGrammar?: Dispatch<SetStateAction<boolean>> | undefined;
      onHint?: () => any;
      isCompleted?: boolean;
      isLoading?: boolean;
      nextButton?: string | undefined;
      onCheck?: () => any;
      audio?: HTMLAudioElement;
      rightButton?: ReactElement;
    }
> = ({
  task,
  sentId,
  storyId,
  audio,
  onHint,
  setDictOpened,
  showGrammar,
  isCompleted,
  onCheck,
  onNext,
  children,
  justify = "space-between",
  lessonId,
  isLoading,
  rightButton,
  nextButton,
  ...rest
}) => {
  const location = useLocation();
  const positionAbsolute = location.pathname.includes("/review/");

  const isBeginner = useIsBeginner();

  const user = useContext(UserContext);

  const { data: userProps } = useSuspenseQuery({
    queryKey: ["props", user?.id],
    queryFn: API.user.getProps,
    staleTime: Infinity,
  });

  const nextButtonText = replaceTemplates(nextButton, userProps);

  return (
    <div className={cx(styles.taskPanel, [{ [styles.taskPanel_ios]: isIOS, [styles.taskPanel_absolute]: positionAbsolute }])} {...rest}>
      <Flex justify={justify} align={"center"} className={styles.content}>
        <ReportFeedback task={task} sentId={sentId} storyId={storyId} lessonId={lessonId} />

        {isCompleted ? (
          <Button
            onClick={onNext}
            icon={<CheckOutlined />}
            className={cx(styles.btn_next, styles.completed)}
            type={"primary"}
            shape={"round"}
          >
            {nextButtonText ?? (isBeginner ? "далее" : "next")}
          </Button>
        ) : children ? (
          children
        ) : onCheck ? (
          <Button loading={isLoading} type={"primary"} shape={"round"} onClick={() => onCheck()}>
            {isBeginner ? "проверить" : "check"}
          </Button>
        ) : (
          <Button disabled icon={<CheckOutlined />} className={cx(styles.btn_next, { [styles.isMobile]: isMobile })} type={"primary"}>
            {nextButtonText ?? (isBeginner ? "далее" : "next")}
          </Button>
        )}
        {rightButton || (
          <SentenceTaskHelpButton
            sentId={sentId}
            storyId={storyId}
            isCompleted={isCompleted}
            openDictionary={setDictOpened}
            onHintAnswer={onHint}
            audio={audio}
            showGrammar={showGrammar}
          />
        )}
      </Flex>
    </div>
  );
};

export default TaskPanel;
